/**
 * Wishlist Storage Utilities
 *
 * Cookie-based persistence for wishlist items per country.
 * The cookie is the source of truth - no localStorage.
 *
 * On login, the server fetches the user's wishlist and sets the cookie.
 * All client-side operations read/write to the cookie.
 */

import { IS_BROWSER } from '@/lib/const';
import { devLog } from '@/lib/utils/devLog';

import {
  clearWishlistCookie,
  getWishlistCookie,
  setWishlistCookie,
  type WishlistCookieItem,
} from './cookies.client';
import type { SkuWishlistMap, WishlistItem } from './types';

// =============================================================================
// Cookie <-> SkuWishlistMap Conversion
// =============================================================================

/**
 * Convert cookie items array to SkuWishlistMap
 */
function cookieToSkuMap(items: WishlistCookieItem[]): SkuWishlistMap {
  const map: SkuWishlistMap = {};
  for (const item of items) {
    map[item.sku] = {
      sku: item.sku,
      type: item.type || 'watch',
      price: 0, // Price is fetched from Algolia when displayed
      theCurrency: 'USD',
    };
  }
  return map;
}

/**
 * Convert SkuWishlistMap to cookie items array
 */
function skuMapToCookie(map: SkuWishlistMap): WishlistCookieItem[] {
  return Object.values(map).map((item) => ({
    sku: item.sku,
    type: item.type || 'watch',
  }));
}

// =============================================================================
// Storage Operations (Cookie-based)
// =============================================================================

/**
 * Get wishlist items for a specific country from cookie
 */
export function getLocalWishlistByCountry(countryCode: string): SkuWishlistMap {
  if (!IS_BROWSER) return {};

  const normalizedCountry = countryCode.toUpperCase();
  const cookieItems = getWishlistCookie(normalizedCountry);

  devLog('[Wishlist Storage] Getting items for country:', {
    countryCode: normalizedCountry,
    itemCount: cookieItems.length,
  });

  return cookieToSkuMap(cookieItems);
}

/**
 * Update wishlist for a specific country in cookie
 */
export function updateLocalWishlistByCountry(
  countryCode: string,
  countryWishlist: SkuWishlistMap
): void {
  if (!IS_BROWSER) return;

  const normalizedCountry = countryCode.toUpperCase();
  const cookieItems = skuMapToCookie(countryWishlist);

  devLog('[Wishlist Storage] Updating cookie for country:', {
    countryCode: normalizedCountry,
    itemCount: cookieItems.length,
  });

  setWishlistCookie(normalizedCountry, cookieItems);
}

/**
 * Add an item to the country's wishlist in cookie
 */
export function addItemToLocalWishlist(countryCode: string, item: WishlistItem): boolean {
  const currentWishlist = getLocalWishlistByCountry(countryCode);

  // Already exists
  if (item.sku in currentWishlist) {
    devLog('[Wishlist Storage] Item already in wishlist:', item.sku);
    return false;
  }

  const newWishlist: SkuWishlistMap = {
    ...currentWishlist,
    [item.sku]: {
      ...item,
      addedAt: Date.now(),
    },
  };

  devLog(
    '[Wishlist Storage] Adding item:',
    item.sku,
    'Total items:',
    Object.keys(newWishlist).length
  );
  updateLocalWishlistByCountry(countryCode, newWishlist);
  return true;
}

/**
 * Remove an item from the country's wishlist in cookie
 */
export function removeItemFromLocalWishlist(countryCode: string, sku: string): boolean {
  const currentWishlist = getLocalWishlistByCountry(countryCode);

  // Doesn't exist
  if (!(sku in currentWishlist)) {
    devLog('[Wishlist Storage] Item not in wishlist:', sku);
    return false;
  }

  const { [sku]: _, ...newWishlist } = currentWishlist;
  devLog(
    '[Wishlist Storage] Removing item:',
    sku,
    'Remaining items:',
    Object.keys(newWishlist).length
  );
  updateLocalWishlistByCountry(countryCode, newWishlist);
  return true;
}

/**
 * Check if an item exists in the country's wishlist
 */
export function isItemInLocalWishlist(countryCode: string, sku: string): boolean {
  const currentWishlist = getLocalWishlistByCountry(countryCode);
  const exists = sku in currentWishlist;
  devLog('[Wishlist Storage] Checking if in wishlist:', { sku, exists, countryCode });
  return exists;
}

/**
 * Get the count of items in a country's wishlist
 */
export function getLocalWishlistCount(countryCode: string): number {
  const currentWishlist = getLocalWishlistByCountry(countryCode);
  return Object.keys(currentWishlist).length;
}

/**
 * Get all SKUs from a country's wishlist
 */
export function getLocalWishlistSkus(countryCode: string): string[] {
  const currentWishlist = getLocalWishlistByCountry(countryCode);
  return Object.keys(currentWishlist);
}

/**
 * Clear wishlist for a specific country
 */
export function clearLocalWishlistByCountry(countryCode: string): void {
  const normalizedCountry = countryCode.toUpperCase();
  clearWishlistCookie(normalizedCountry);
}

/**
 * Clear all wishlists (all country cookies)
 */
export function clearAllLocalWishlists(): void {
  if (!IS_BROWSER) return;

  // Clear all wishlist cookies (pass undefined to clear all)
  clearWishlistCookie();
}

/**
 * Get the full wishlist map (for backwards compatibility)
 * Returns a map with the current country's wishlist
 */
export function getLocalWishlist(): Record<string, SkuWishlistMap> {
  // This function was used for localStorage which stored all countries
  // For cookies, we only have one country at a time, so return empty
  // The actual country-specific function is getLocalWishlistByCountry
  return {};
}

/**
 * Merge two wishlist maps, preferring items from the second map
 */
export function mergeWishlists(base: SkuWishlistMap, overlay: SkuWishlistMap): SkuWishlistMap {
  return { ...base, ...overlay };
}

/**
 * Sync wishlist from cookie to localStorage - NO-OP
 * This function is kept for backwards compatibility but does nothing
 * since we no longer use localStorage.
 */
export function syncCookieToLocalStorage(_countryCode: string): void {
  // NO-OP - cookies are now the source of truth, no localStorage sync needed
  devLog('[Wishlist Storage] syncCookieToLocalStorage called but is a no-op');
}
